import { Box, Button, Grid, InputAdornment, TextField, Typography, } from '@mui/material';
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom'
import Spinner from '../../../Component/spinner/spinner';
import { toast } from 'react-toastify';
import { selectCurrentScope } from "../../../app/auth/authSlice";
import { useSelector } from "react-redux";
import placeholder from '../../../assets/images/placeholder/placeholder.png'
import { useLazyGetBannerByIdQuery, useSaveBannerMutation, useUpdateBannerByIdMutation } from '../../../app/services/bannerServices';
import ImageCropModal from '../../../Component/ImageCropModal/ImageCropModal';

const AddEditBannerForm = () => {
    const { bannerContentId } = useParams();
    const [loading, setLoading] = useState(false);
    const {
        register,
        setValue,
        handleSubmit,
        formState: { isValid, errors },
        reset,
        setError,
        clearErrors,
        watch
    } = useForm({
        defaultValues: {
            text: '',
            buttonName: '',
            buttonLink: '',
            desktopBannerImage: '',
            mobileBannerImage: ''
        },
        mode: 'onChange'
    });
    const [saveBanner] = useSaveBannerMutation();
    const [updateBanner] = useUpdateBannerByIdMutation();
    const [getBannerById] = useLazyGetBannerByIdQuery();
    const memberScope = useSelector(selectCurrentScope);
    const navigate = useNavigate();
    const [selectedDesktopImage, setSelectedDesktopImage] = useState();
    const [selectedMobileImage, setSelectedMobileImage] = useState();

    // Crop modal state
    const [desktopCropSrc, setDesktopCropSrc] = useState(null);
    const [mobileCropSrc, setMobileCropSrc] = useState(null);

    // get Banner By Id
    useEffect(() => {
        if (bannerContentId) {
            setLoading(true);
            getBannerById(bannerContentId).then((res) => {
                if (res.data?.status) {
                    setLoading(false);
                    const data = res.data?.data;
                    if (data.desktopBannerImage.length > 0) {
                        const cacheBustedUrl = `${data.desktopBannerImage}?t=${new Date().getTime()}`;
                        setValue("desktopBannerImage", cacheBustedUrl);
                    } else {
                        setValue("desktopBannerImage", "");
                    }
                    if (data.mobileBannerImage.length > 0) {
                        const cacheBustedUrl = `${data.mobileBannerImage}?t=${new Date().getTime()}`;
                        setValue("mobileBannerImage", cacheBustedUrl);
                    } else {
                        setValue("mobileBannerImage", "");
                    }
                    setValue("text", data.text);
                    setValue("buttonName", data.buttonName);
                    setValue("buttonLink", data.buttonLink);
                } else {
                    setLoading(false);
                }
            });
        }
    }, [bannerContentId, getBannerById, setValue]);

    // ── Desktop image handlers ──
    const handleDesktopFilePick = (event) => {
        const file = event.target.files[0];
        event.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('desktopImage', { message: 'Please select an image file' });
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setError('desktopImage', { message: 'File size exceeds 5 MB' });
            return;
        }
        clearErrors('desktopImage');
        const reader = new FileReader();
        reader.onload = (e) => setDesktopCropSrc(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleDesktopCropApply = (base64) => {
        setSelectedDesktopImage(base64);
        setValue('desktopBannerImage', base64);
        clearErrors('desktopImage');
        setDesktopCropSrc(null);
    };

    // ── Mobile image handlers ──
    const handleMobileFilePick = (event) => {
        const file = event.target.files[0];
        event.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('mobileImage', { message: 'Please select an image file' });
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setError('mobileImage', { message: 'File size exceeds 5 MB' });
            return;
        }
        clearErrors('mobileImage');
        const reader = new FileReader();
        reader.onload = (e) => setMobileCropSrc(e.target.result);
        reader.readAsDataURL(file);
    };

    const handleMobileCropApply = (base64) => {
        setSelectedMobileImage(base64);
        setValue('mobileBannerImage', base64);
        clearErrors('mobileImage');
        setMobileCropSrc(null);
    };

    // ── Submit / Update ──
    const onFormSubmit = (formData) => {
        if (isValid) {
            setLoading(true);
            if (bannerContentId) {
                let updateData;
                const base64Regex = /^data:image\/([a-zA-Z]*);base64,([^"]*)$/;
                if (base64Regex.test(formData.imageUrl)) {
                    updateData = { id: bannerContentId, formData };
                } else {
                    updateData = { id: bannerContentId, formData };
                }
                updateBanner(updateData).then((res) => {
                    if (res.data?.status) {
                        toast.success("Banner Content Updated Successfully!!!");
                        setLoading(false);
                        if (memberScope === "PORTAL_SUPER_ADMIN") {
                            navigate("/admin/banner-content-table");
                        } else if (memberScope === "PORTAL_ADMIN") {
                            navigate("/assistantAdmin/banner-content-table");
                        }
                        reset();
                    } else {
                        setLoading(false);
                        toast.error(res.data?.err?.message);
                    }
                });
            } else {
                saveBanner(formData).then((res) => {
                    if (res.data?.status) {
                        toast.success("Banner Content Added Successfully!!!");
                        setLoading(false);
                        if (memberScope === "PORTAL_SUPER_ADMIN") {
                            navigate("/admin/banner-content-table");
                        } else if (memberScope === "PORTAL_ADMIN") {
                            navigate("/assistantAdmin/banner-content-table");
                        }
                        reset();
                    } else {
                        setLoading(false);
                        toast.error(res.data?.err?.message);
                    }
                });
            }
        }
    };

    return (
        <>
            <div className="pt-5">
                <div className="chapter-heading">
                    <h2 className="text-center title-font text-light py-3 mt-1">
                        {bannerContentId ? "Edit Banner Content" : "Add Banner Content"}
                    </h2>
                </div>
            </div>

            {/* ── Desktop Image Crop Modal ── */}
            <ImageCropModal
                open={!!desktopCropSrc}
                imageSrc={desktopCropSrc}
                onCancel={() => setDesktopCropSrc(null)}
                onCrop={handleDesktopCropApply}
                outputWidth={1920}
                outputHeight={620}
                isSquareCrop={true}
                title="Crop Desktop Banner Image"
            />

            {/* ── Mobile Image Crop Modal ── */}
            <ImageCropModal
                open={!!mobileCropSrc}
                imageSrc={mobileCropSrc}
                onCancel={() => setMobileCropSrc(null)}
                onCrop={handleMobileCropApply}
                outputWidth={365}
                outputHeight={455}
                isSquareCrop={true}
                title="Crop Mobile Banner Image"
            />

            {loading ? (
                <Spinner />
            ) : (
                <Box
                    component={"form"}
                    className="d-flex flex-column align-items-center p-md-5 p-3 overallPadding"
                    onSubmit={handleSubmit(onFormSubmit)}
                >
                    <div className="text-end w-100 mb-3">
                        <Button
                            onClick={() => window.history.back()}
                            variant="contained"
                            className="menus-color"
                            style={{ float: "right" }}
                        >
                            Back
                        </Button>
                    </div>

                    <Grid container rowSpacing={3} columnSpacing={3}>
                        {/* ── Desktop Banner Image ── */}
                        <Grid item md={6} xs={12} className='d-flex align-items-end'>
                            <div className='d-flex flex-column justify-content-between row-gap-3 w-100'>
                                {/* Hidden file input */}
                                <input
                                    id="desktopBannerFileInput"
                                    type="file"
                                    accept="image/jpeg,image/png"
                                    style={{ display: 'none' }}
                                    onChange={handleDesktopFilePick}
                                />
                                {/* Invisible RHF registration for required validation */}
                                <input type="hidden" {...register('desktopImage', {
                                    required: { value: !watch('desktopBannerImage'), message: 'Please select an image for desktop' }
                                })} />

                                {/* Preview */}
                                <img
                                    src={selectedDesktopImage || (watch("desktopBannerImage") ? watch("desktopBannerImage") : placeholder)}
                                    alt="Desktop banner"
                                    className='w-75'
                                    style={{ objectFit: "scale-down" }}
                                />
                                {/* Error */}
                                {!!errors?.desktopImage && (
                                    <p className='error-message mb-0'>{errors?.desktopImage?.message}</p>
                                )}
                                {/* Pick / Choose button */}
                                <div>
                                    <Button
                                        variant="contained"
                                        className="menus-color"
                                        style={{ whiteSpace: "nowrap" }}
                                        type="button"
                                        onClick={() => document.getElementById('desktopBannerFileInput').click()}
                                    >
                                        Choose Desktop Image <span style={{ color: "white" }}>*</span>
                                    </Button>
                                </div>
                                {/* <div className='d-flex gap-1'>
                                    <InfoIcon style={{ color: '#611f1e', fontSize: 14, marginTop: 3 }} />
                                    <p style={{ fontSize: '14px', display: 'flex', marginBottom: '0px' }}>
                                        Recommended: 1920 × 620 px. Image will be cropped to fit.
                                    </p>
                                </div> */}
                            </div>
                        </Grid>

                        {/* ── Mobile Banner Image ── */}
                        <Grid item md={6} xs={12}>
                            <div className='d-flex flex-column justify-content-between w-100 row-gap-3'>
                                {/* Hidden file input */}
                                <input
                                    id="mobileBannerFileInput"
                                    type="file"
                                    accept="image/jpeg,image/png"
                                    style={{ display: 'none' }}
                                    onChange={handleMobileFilePick}
                                />
                                {/* Invisible RHF registration for required validation */}
                                <input type="hidden" {...register('mobileImage', {
                                    required: { value: !watch('mobileBannerImage'), message: 'Please select an image for mobile' }
                                })} />

                                {/* Preview */}
                                <img
                                    src={selectedMobileImage || (watch("mobileBannerImage") ? watch("mobileBannerImage") : placeholder)}
                                    alt="Mobile Banner"
                                    style={{ objectFit: "scale-down", width: "130px" }}
                                />
                                {/* Error */}
                                {!!errors?.mobileImage && (
                                    <p className='error-message mb-0'>{errors?.mobileImage?.message}</p>
                                )}
                                {/* Pick / Choose button */}
                                <div>
                                    <Button
                                        variant="contained"
                                        className="menus-color"
                                        style={{ whiteSpace: "nowrap" }}
                                        type="button"
                                        onClick={() => document.getElementById('mobileBannerFileInput').click()}
                                    >
                                        Choose Mobile Image <span style={{ color: "white" }}>*</span>
                                    </Button>
                                </div>
                                {/* <div className='d-flex gap-1'>
                                    <InfoIcon style={{ color: '#611f1e', fontSize: 14, marginTop: 3 }} />
                                    <p style={{ fontSize: '14px', display: 'flex', alignItems: 'center', marginBottom: '0px' }}>
                                        Recommended: 365 × 455 px. Image will be cropped to fit.
                                    </p>
                                </div> */}
                            </div>
                        </Grid>

                        <Grid item lg={4} md={6} sm={12} xs={12} className='d-flex align-items-start'>
                            <TextField
                                id="outlined-basic"
                                label={<Typography className="text-dark">
                                    Banner Text<span style={{ color: "red" }}>*</span>
                                </Typography>}
                                InputLabelProps={{ shrink: true }}
                                className="mt-3"
                                multiline
                                inputProps={{ maxLength: 150 }}
                                {...register("text", { required: "Please enter banner content text" })}
                                error={!!errors?.text}
                                placeholder="Enter banner text"
                                helperText={errors?.text?.message} fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <span style={{ position: 'absolute', bottom: '0', right: '5px' }} className='fw-bold text-primary'>
                                                {150 - (watch('text') ? watch('text').length : 0)}
                                            </span>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        <Grid item lg={4} md={6} sm={12} xs={12} className='d-flex align-items-start mt-3'>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="buttonText"
                                label={
                                    <Typography className="text-dark">
                                        Button Text <span style={{ color: "red" }}>*</span>
                                    </Typography>
                                }
                                placeholder="Please enter button text"
                                style={{ height: "max-content" }}
                                {...register("buttonName", {
                                    required: "Please enter button text",
                                    maxLength: { value: 300, message: "Text cannot exceed 300 characters" },
                                })}
                                inputProps={{ maxLength: 300 }}
                                className="form-control"
                                error={!!errors?.buttonName}
                                helperText={errors?.buttonName?.message}
                            />
                        </Grid>

                        <Grid item lg={4} md={6} sm={12} xs={12} className="d-flex align-items-start mt-3">
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                id="buttonLink"
                                label={
                                    <Typography className="text-dark">
                                        Button Link <span style={{ color: "red" }}>*</span>
                                    </Typography>
                                }
                                placeholder="Example:www.sample.com"
                                style={{ height: "max-content" }}
                                {...register("buttonLink", {
                                    required: "Please enter button link",
                                    pattern: {
                                        value: /^(https?:\/\/)?((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})|(\d{1,3}\.){3}\d{1,3})(:[0-9]+)?(\/[\w .-]*)*(\?.*)?$/,
                                        message: "Please enter a valid link",
                                    },
                                })}
                                className="form-control"
                                error={!!errors?.buttonLink}
                                helperText={errors?.buttonLink?.message}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <>
                        <Button variant="contained" className="menus-color mt-5" type="submit">
                            {bannerContentId ? "Update" : "Submit"}
                        </Button>
                    </>
                </Box>
            )}
        </>
    )
}

export default AddEditBannerForm